import type { MockedResponse } from "@apollo/client/testing";
import { COMMISSION_CAMPAIGNS_QUERY } from "../campaigns/queries";
import { COMMISSION_ENROLLMENTS_QUERY } from "../enrollments/queries";
import { CREATE_COMMISSION_CAMPAIGN_MUTATION } from "../campaigns/mutations";
import {
  COMMISSION_PARTNER_QUERY,
  COMMISSION_PARTNERS_QUERY
} from "@/pages/people/operations/golf-retirement-plus/partners/queries";
import { CREATE_COMMISSION_PARTNER_MUTATION } from "@/pages/people/operations/golf-retirement-plus/partners/mutations";
import { COMMISSION_PROGRAMS_QUERY } from "../programs/queries";
import { buildConnection } from "./connection";
import {
  commissionCampaignFixtures,
  commissionEnrollmentFixtures,
  commissionPartnerFixtures,
  commissionProgramFixtures
} from "./fixtures";
import { createCatalogMock, withTypenames, type MockResolver } from "./mockUtils";

function buildPartnerCampaignsMock(partnerId: string) {
  const nodes = commissionCampaignFixtures
    .filter((campaign) => campaign.partner.id === partnerId)
    .map((campaign) =>
      withTypenames(
        {
          id: campaign.id,
          editionYear: campaign.editionYear,
          status: campaign.status,
          startsAt: campaign.startsAt,
          endsAt: campaign.endsAt,
          program: withTypenames(campaign.program, "BackofficeCommissionProgramSummary")
        },
        "BackofficeCommissionCampaign"
      )
    );

  return buildConnection("BackofficeCommissionCampaignConnection", nodes);
}

export const commissionProgramsMockData = {
  commissionPrograms: buildConnection(
    "BackofficeCommissionProgramConnection",
    commissionProgramFixtures.map((node) => withTypenames(node, "BackofficeCommissionProgram"))
  )
};

export const commissionPartnersMockData = {
  commissionPartners: buildConnection(
    "BackofficeCommissionPartnerConnection",
    commissionPartnerFixtures.map((node) =>
      withTypenames(
        {
          ...node,
          campaigns: buildPartnerCampaignsMock(node.id)
        },
        "BackofficeCommissionPartner"
      )
    )
  )
};

export function buildCampaignsMockData(variables: Record<string, unknown> = {}) {
  const partnerId = variables.partnerId ? String(variables.partnerId) : undefined;
  const nodes = commissionCampaignFixtures
    .filter((node) => !partnerId || node.partner.id === partnerId)
    .map((node) => ({
      __typename: "BackofficeCommissionCampaign",
      ...node,
      partner: {
        ...withTypenames(node.partner, "BackofficeCommissionPartner"),
        currentIngestRecipe: node.partner.currentIngestRecipe
          ? {
              __typename: "BackofficeCommissionIngestRecipe",
              id: node.partner.currentIngestRecipe.id,
              status: "CURRENT"
            }
          : null
      },
      program: withTypenames(node.program, "BackofficeCommissionProgramSummary")
    }));

  return {
    commissionCampaigns: buildConnection("BackofficeCommissionCampaignConnection", nodes)
  };
}

export const commissionCampaignsMockData = buildCampaignsMockData();

export const commissionEnrollmentsMockData = {
  commissionEnrollments: buildConnection(
    "BackofficeCommissionEnrollmentConnection",
    commissionEnrollmentFixtures.map((node) => ({
      __typename: "BackofficeCommissionEnrollment",
      ...node,
      person: node.person ? withTypenames(node.person, "BackofficeCommissionEnrollmentPerson") : null,
      program: withTypenames(node.program, "BackofficeCommissionProgramSummary")
    }))
  )
};

export const commissionProgramsApolloMock = createCatalogMock(COMMISSION_PROGRAMS_QUERY, commissionProgramsMockData);
export const commissionPartnersApolloMock = createCatalogMock(COMMISSION_PARTNERS_QUERY, commissionPartnersMockData);

export const commissionPartnerApolloMock = {
  request: { query: COMMISSION_PARTNER_QUERY },
  maxUsageCount: Infinity,
  variableMatcher: () => true,
  result: (variables: { id?: string }) => {
    const partner =
      commissionPartnerFixtures.find((node) => node.id === String(variables.id)) ?? commissionPartnerFixtures[0];

    return {
      data: {
        commissionPartner: withTypenames(
          {
            ...partner,
            campaigns: buildPartnerCampaignsMock(partner.id)
          },
          "BackofficeCommissionPartner"
        )
      }
    };
  }
};
export const commissionCampaignsApolloMock = createCatalogMock(COMMISSION_CAMPAIGNS_QUERY, commissionCampaignsMockData);
export const commissionEnrollmentsApolloMock = createCatalogMock(
  COMMISSION_ENROLLMENTS_QUERY,
  commissionEnrollmentsMockData
);

export const createCommissionPartnerApolloMock: MockedResponse = {
  request: { query: CREATE_COMMISSION_PARTNER_MUTATION },
  variableMatcher: () => true,
  result: {
    data: {
      commissionCreatePartner: {
        partner: withTypenames(
          {
            id: "commission-partner-created",
            name: "Created Partner",
            legacyId: "SP-NEW",
            status: "ACTIVE"
          },
          "BackofficeCommissionPartner"
        )
      }
    }
  }
};

export const createCommissionCampaignApolloMock: MockedResponse = {
  request: { query: CREATE_COMMISSION_CAMPAIGN_MUTATION },
  variableMatcher: () => true,
  result: {
    data: {
      commissionCreateCampaign: {
        campaign: withTypenames(
          {
            id: "commission-campaign-created",
            editionYear: 2026,
            status: "DRAFT",
            payoutCadence: "QUARTERLY",
            eligibilityRules: [],
            startsAt: "2026-01-01T00:00:00Z",
            endsAt: "2026-12-31T23:59:59Z",
            partner: withTypenames(
              { id: "commission-partner-acme", name: "Acme Golf Sponsors" },
              "BackofficeCommissionPartner"
            ),
            program: withTypenames(
              { id: "commission-program-grp", name: "Golf Retirement Plus", slug: "grp" },
              "BackofficeCommissionProgramSummary"
            )
          },
          "BackofficeCommissionCampaign"
        )
      }
    }
  }
};

export const catalogApolloMocks: MockedResponse[] = [
  commissionProgramsApolloMock,
  commissionPartnersApolloMock,
  commissionPartnerApolloMock,
  commissionCampaignsApolloMock,
  commissionEnrollmentsApolloMock,
  createCommissionPartnerApolloMock,
  createCommissionCampaignApolloMock
];

export const catalogMockResults: Record<string, Record<string, unknown>> = {
  CommissionPrograms: commissionProgramsMockData,
  CommissionPartners: commissionPartnersMockData,
  CommissionCampaigns: commissionCampaignsMockData,
  CommissionEnrollments: commissionEnrollmentsMockData
};

export const catalogVariableMockResolvers: Record<string, MockResolver> = {
  CommissionPartner: (variables) => {
    const partner =
      commissionPartnerFixtures.find((node) => node.id === String(variables.id)) ?? commissionPartnerFixtures[0];

    return {
      commissionPartner: withTypenames(partner, "BackofficeCommissionPartner")
    };
  },
  CommissionCampaigns: (variables) => buildCampaignsMockData(variables),
  CreateCommissionPartner: () => ({
    commissionCreatePartner: {
      partner: withTypenames(
        {
          id: "commission-partner-created",
          name: "Created Partner",
          legacyId: "SP-NEW",
          status: "ACTIVE"
        },
        "BackofficeCommissionPartner"
      )
    }
  }),
  CreateCommissionCampaign: () => ({
    commissionCreateCampaign: {
      campaign: withTypenames(
        {
          id: "commission-campaign-created",
          editionYear: 2026,
          status: "DRAFT",
          payoutCadence: "QUARTERLY",
          eligibilityRules: [],
          startsAt: "2026-01-01T00:00:00Z",
          endsAt: "2026-12-31T23:59:59Z",
          partner: withTypenames(
            { id: "commission-partner-acme", name: "Acme Golf Sponsors" },
            "BackofficeCommissionPartner"
          ),
          program: withTypenames(
            { id: "commission-program-grp", name: "Golf Retirement Plus", slug: "grp", status: "ACTIVE" },
            "BackofficeCommissionProgramSummary"
          )
        },
        "BackofficeCommissionCampaign"
      )
    }
  }),
  PersonGrpContext: (variables) => {
    const pgaId = String(variables.pgaId);
    const enrollment = commissionEnrollmentFixtures.find((node) => node.person?.pgaId === pgaId);

    return {
      person: withTypenames(
        {
          id: enrollment?.person?.id ?? `person-${pgaId}`,
          displayName: enrollment?.person?.displayName ?? `Member ${pgaId}`,
          pgaId
        },
        "CRMPerson"
      )
    };
  }
};
